'use client'

import { useSearchParams } from 'next/navigation'
import { useMemo } from 'react'

type ServiceLevelParam = 'ground' | 'exground' | 'nextday' | '2day' | '3day' | '4day'

type ServiceLevelSystemName =
  | 'DOMESTIC_GROUND'
  | 'DOMESTIC_EXPEDITED_GROUND'
  | 'DOMESTIC_1_DAY'
  | 'DOMESTIC_2_DAY'
  | 'DOMESTIC_3_DAY'
  | 'DOMESTIC_4_DAY'

const ServiceLevels: Record<ServiceLevelParam, ServiceLevelSystemName> = {
  ground: 'DOMESTIC_GROUND',
  exground: 'DOMESTIC_EXPEDITED_GROUND',
  nextday: 'DOMESTIC_1_DAY',
  '2day': 'DOMESTIC_2_DAY',
  '3day': 'DOMESTIC_3_DAY',
  '4day': 'DOMESTIC_4_DAY',
}

type ShipDay = 'sameday' | 'nextday' | Date

export interface ShipPoint {
  address_1?: string
  contact?: string
  city?: string
  state?: string
  country_code?: string
  zip?: string
  attention_name?: string
  phone_number?: string
  company_name?: string
}

export interface ShipPageParams {
  outboundServiceLevel?: ServiceLevelSystemName
  returnServiceLevel?: ServiceLevelSystemName

  protectionLevel?: string
  shipDay?: ShipDay

  productLineQuantities?: {
    golf?: number
    luggage?: number
    ski?: number
    snowboard?: number
    snowboot?: number
    bike?: number
    box?: number
    trunk?: number
  }

  products?: string[]

  productPackagings?: {
    golf?: string
    ski?: string
    snowboard?: string
    snowboot?: string
    luggage?: string
  }

  coupon?: string
  oneway?: boolean

  traveler?: {
    name?: string
    email?: string
    phone?: string
  }

  referral?: {
    network?: string
    company?: string
    agent?: string
  }

  shipPoints?: {
    origin?: ShipPoint
    destination?: ShipPoint
  }

  facilities?: {
    origin?: string
    destination?: string
  }
}

type ParamType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'array'
  | 'shipDay'
  | 'outboundServiceLevel'
  | 'returnServiceLevel'

interface ParamConfigItem {
  key: keyof ShipPageParams | string
  type: ParamType
}

const paramTypes: Record<string, ParamType> = {
  // Strings
  osl: 'outboundServiceLevel',
  rsl: 'returnServiceLevel',
  pl: 'string',
  sd: 'shipDay',
  golfpackage: 'string',
  skipackage: 'string',
  snowboardpackage: 'string',
  snowbootpackage: 'string',
  luggagepackage: 'string',
  coupon: 'string',

  travname: 'string',
  travemail: 'string',
  travphone: 'string',

  refnetwork: 'string',
  refcompany: 'string',
  refagent: 'string',

  o_address: 'string',
  o_contact: 'string',
  o_city: 'string',
  o_state: 'string',
  o_country: 'string',
  o_zip: 'string',
  o_company: 'string',
  d_address: 'string',
  d_contact: 'string',
  d_city: 'string',
  d_state: 'string',
  d_country: 'string',
  d_zip: 'string',
  d_company: 'string',

  shipping_from: 'string',
  shipping_to: 'string',

  // Numbers
  golfqty: 'number',
  luggageqty: 'number',
  skiqty: 'number',
  snowboardqty: 'number',
  snowbootqty: 'number',
  bikeqty: 'number',
  boxqty: 'number',
  trunkqty: 'number',

  // Booleans
  oneway: 'boolean',

  // Arrays
  product: 'array',
}
const paramConfig: ParamConfigItem[] = Object.entries(paramTypes).map(([key, type]) => ({
  key,
  type,
}))

const groups: Record<string, Record<string, string>> = {
  traveler: {
    travname: 'name',
    travemail: 'email',
    travphone: 'phone',
  },
  referral: {
    refnetwork: 'network',
    refcompany: 'company',
    refagent: 'agent',
  },
  shipPoints: {
    o_address: 'origin.address_1',
    o_contact: 'origin.contact',
    o_city: 'origin.city',
    o_state: 'origin.state',
    o_country: 'origin.country_code',
    o_zip: 'origin.zip',
    o_company: 'origin.company_name',
    d_address: 'destination.address_1',
    d_contact: 'destination.contact',
    d_city: 'destination.city',
    d_state: 'destination.state',
    d_country: 'destination.country_code',
    d_zip: 'destination.zip',
    d_company: 'destination.company_name',
  },
  facilities: {
    shipping_from: 'origin',
    shipping_to: 'destination',
  },
  productLineQuantities: {
    golfqty: 'golf',
    luggageqty: 'luggage',
    skiqty: 'ski',
    snowboardqty: 'snowboard',
    snowbootqty: 'snowboot',
    bikeqty: 'bike',
    boxqty: 'box',
    trunkqty: 'trunk',
  },
}

const paramKeyMap: Record<string, keyof ShipPageParams> = {
  osl: 'outboundServiceLevel',
  rsl: 'returnServiceLevel',
  pl: 'protectionLevel',
  sd: 'shipDay',
  product: 'products',
}

function coerceParam(value: string, type: ParamType): unknown {
  switch (type) {
    case 'string':
      return value
    case 'number':
      // eslint-disable-next-line no-case-declarations
      const n = parseInt(value, 10)
      return isNaN(n) ? undefined : n
    case 'boolean':
      if (value === 'true') return true
      if (value === 'false') return false
      return undefined
    case 'array':
      return value
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
    case 'shipDay':
      if (value === 'sameday' || value === 'nextday') {
        return value
      }

      // eslint-disable-next-line no-case-declarations
      const match = /^(\d{2})_(\d{2})_(\d{4})$/.exec(value)

      if (match) {
        const [, mm, dd, yyyy] = match

        // build date in UTC to avoid timezone shift
        const date = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)))

        return isNaN(date.getTime()) ? undefined : date
      }

      return undefined
    case 'outboundServiceLevel':
    case 'returnServiceLevel':
      return ServiceLevels[value as ServiceLevelParam]
    default:
      return undefined
  }
}

function assignNestedValue(target: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.')

  let current = target

  keys.forEach((key, i) => {
    if (i === keys.length - 1) {
      current[key] = value
    } else {
      current[key] ??= {}
      current = current[key] as Record<string, unknown>
    }
  })
}

export function useShipParams(): ShipPageParams {
  const searchParams = useSearchParams()

  return useMemo(() => {
    if (!searchParams) return {}

    const result: Partial<ShipPageParams> & Record<string, unknown> = {}

    for (const { key, type } of paramConfig) {
      const rawValue = searchParams.get(key)

      if (!rawValue) continue

      const coerced = coerceParam(rawValue, type)

      if (coerced !== undefined) {
        const mappedKey = paramKeyMap[key] ?? key

        result[mappedKey as string] = coerced
      }
    }

    for (const [groupName, keyMap] of Object.entries(groups)) {
      const groupObj: Record<string, unknown> = {}

      let hasGroupData = false

      for (const [paramKey, nestedPath] of Object.entries(keyMap)) {
        if (result[paramKey] !== undefined) {
          assignNestedValue(groupObj, nestedPath, result[paramKey])

          delete result[paramKey]

          hasGroupData = true
        }
      }

      if (hasGroupData) {
        result[groupName] = groupObj
      }
    }

    return result as ShipPageParams
  }, [searchParams])
}
